// Sensitivity Grid — PSD §7.1 (F7.1, F4.3). Analysis tab.

import { useState, useMemo } from 'react';
import type { ValuationInputs } from '@backsolve/shared-types';
import {
  computeSensitivityGrid,
  type SensitivityVar,
} from '@backsolve/shared-logic/sensitivity';

interface SensitivityGridProps {
  inputs: ValuationInputs;
}

const VARS: { value: SensitivityVar; label: string }[] = [
  { value: 'volatility', label: 'Volatility' },
  { value: 'timeToLiquidity', label: 'Time to Liquidity' },
  { value: 'dlomPercentage', label: 'DLOM %' },
  { value: 'totalEquityValue', label: 'Total Equity Value' },
];

export function SensitivityGrid({ inputs }: SensitivityGridProps) {
  const [xVar, setXVar] = useState<SensitivityVar>('volatility');
  const [yVar, setYVar] = useState<SensitivityVar>('timeToLiquidity');

  const { xValues, yValues } = useMemo(() => buildRanges(inputs, xVar, yVar), [inputs, xVar, yVar]);

  const grid = useMemo(
    () => computeSensitivityGrid(inputs, xVar, xValues, yVar, yValues),
    [inputs, xVar, xValues, yVar, yValues],
  );

  const maxValue = useMemo(() => {
    let m = 0;
    for (const row of grid.cells) for (const cell of row) if (cell.fmvPerShare && cell.fmvPerShare > m) m = cell.fmvPerShare;
    return m || 1;
  }, [grid]);

  function colorFor(v: number | null): string {
    if (v === null) return 'var(--color-surface-2)';
    const ratio = v / maxValue;
    const alpha = 0.15 + ratio * 0.6;
    return `rgba(34, 139, 230, ${alpha.toFixed(2)})`;
  }

  return (
    <div className="sensitivity-grid">
      <h3>Sensitivity Analysis</h3>
      <p className="result-caption">
        Heatmap of common FMV per share across two-variable scenarios (&plusmn;20%
        around the current inputs). Darker cells = higher FMV. Each cell re-runs
        the full engine, so the table is the authoritative what-if view.
      </p>

      <div className="form-row">
        <label htmlFor="x-axis">X-Axis:</label>
        <select id="x-axis" value={xVar} onChange={(e) => setXVar(e.target.value as SensitivityVar)}>
          {VARS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
        <label htmlFor="y-axis">Y-Axis:</label>
        <select id="y-axis" value={yVar} onChange={(e) => setYVar(e.target.value as SensitivityVar)}>
          {VARS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      <div className="table-wrap">
        <table className="data-table sensitivity-table">
          <thead>
            <tr>
              <th>{grid.yAxis.label} \ {grid.xAxis.label}</th>
              {grid.xAxis.values.map((v, i) => (
                <th key={i}>{fmtVal(xVar, v)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.cells.map((row, j) => (
              <tr key={j}>
                <td className="row-header">{fmtVal(yVar, grid.yAxis.values[j]!)}</td>
                {row.map((cell, i) => (
                  <td
                    key={i}
                    style={{
                      backgroundColor: colorFor(cell.fmvPerShare),
                      textAlign: 'right',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {cell.fmvPerShare !== null ? `$${cell.fmvPerShare.toFixed(4)}` : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dlom-benchmark mt-4">
        <h4>DLOM Model Benchmarking</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>Model</th>
              <th>DLOM %</th>
            </tr>
          </thead>
          <tbody>
            {grid.benchmarkDlom.map((b) => (
              <tr key={b.model} className={b.model === inputs.discounts.dlomModel ? 'row-selected' : ''}>
                <td>{b.model}</td>
                <td>{(b.pct * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function buildRanges(inputs: ValuationInputs, xVar: SensitivityVar, _yVar: SensitivityVar) {
  function range(center: number, steps: number[] = [-0.2, -0.1, 0, 0.1, 0.2]): number[] {
    return steps.map((s) => Math.max(0.001, center * (1 + s)));
  }

  const baseX = getBase(inputs, xVar);
  const baseY = getBase(inputs, _yVar);

  return { xValues: range(baseX), yValues: range(baseY) };
}

function getBase(inputs: ValuationInputs, v: SensitivityVar): number {
  switch (v) {
    case 'volatility':
      return inputs.assumptions.volatility;
    case 'timeToLiquidity':
      return inputs.assumptions.timeToLiquidity;
    case 'dlomPercentage':
      return inputs.discounts.dlomPercentage || 0.25;
    case 'totalEquityValue':
      return inputs.equityValue.directEquityValue ?? 10_000_000;
  }
}

function fmtVal(v: SensitivityVar, n: number): string {
  switch (v) {
    case 'volatility':
      return `${(n * 100).toFixed(0)}%`;
    case 'timeToLiquidity':
      return `${n.toFixed(1)}y`;
    case 'dlomPercentage':
      return `${(n * 100).toFixed(0)}%`;
    case 'totalEquityValue':
      return `$${(n / 1_000_000).toFixed(1)}M`;
  }
}
